/**
 * Shared auth header helper — Steps 2–4 of shared auth migration.
 *
 * Centralizes token → Authorization header construction for all API utilities.
 *
 * Auth modes (Step 4):
 *   - legacy (default): reads mf_token from localStorage
 *   - clerk: uses injected Clerk getToken() when REACT_APP_USE_CLERK_AUTH=true
 *
 * The Clerk token getter is injected by ClerkTokenBridge (a React provider)
 * via setClerkTokenGetter(). This avoids calling React hooks from utility code.
 *
 * See: docs/architecture/shared-auth-migration-master-plan.md
 *
 * IMPORTANT: getAuthHeaders() is async — required for Clerk's async getToken().
 */

const TOKEN_KEY = 'mf_token';
const USE_CLERK = process.env.REACT_APP_USE_CLERK_AUTH === 'true';

// ── Clerk token getter injection (Step 4) ──────────────────────────────
// Set by ClerkTokenBridge via setClerkTokenGetter().
// When set + USE_CLERK=true, getAuthHeaders() calls this instead of localStorage.
// The getter is an async function that returns a JWT string or null.
let _clerkTokenGetter = null;

/**
 * Inject the Clerk getToken function from a React provider.
 *
 * Called by ClerkTokenBridge on mount. The getter should be:
 *   async () => string | null
 *
 * @param {Function|null} getter - Async function returning Clerk JWT, or null to clear
 */
export function setClerkTokenGetter(getter) {
  _clerkTokenGetter = getter;
}

/**
 * Check if Clerk token getter is currently active.
 * Useful for components that need to know if Clerk bridge is ready.
 */
export function isClerkTokenBridgeActive() {
  return USE_CLERK && typeof _clerkTokenGetter === 'function';
}

/**
 * Get Authorization headers for API requests.
 *
 * @param {Object} [extra={}] - Additional headers to merge
 * @returns {Promise<Object>} Headers object with Authorization if token exists
 *
 * Step 4: When USE_CLERK=true and ClerkTokenBridge has injected a getter,
 * calls the getter for a fresh Clerk JWT. Falls back to legacy mf_token
 * if the getter returns null or if Clerk mode is off.
 */
export async function getAuthHeaders(extra = {}) {
  const headers = { ...extra };

  // ── Clerk path (Step 4) ─────────────────────────────────────────────
  // When Clerk is enabled and the token bridge has injected a getter,
  // use Clerk's session JWT as the Bearer token.
  if (USE_CLERK && typeof _clerkTokenGetter === 'function') {
    try {
      const clerkToken = await _clerkTokenGetter();
      if (clerkToken) {
        headers['Authorization'] = `Bearer ${clerkToken}`;
        return headers;
      }
      // Clerk returned null (not signed in) — fall through to legacy path.
      // This allows anonymous users to still work when Clerk is enabled.
    } catch (err) {
      // Clerk getToken() failed — fall through to legacy path.
      // This can happen during session transitions.
      console.warn('[authHeaders] Clerk getToken() failed, falling back to legacy:', err.message);
    }
  }

  // ── Legacy mf_token path (active default, also fallback for Clerk) ──
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Get the raw auth token (synchronous convenience).
 *
 * Prefer getAuthHeaders() for API calls. This is for callers that
 * need the raw token string (e.g., XMLHttpRequest, EventSource).
 *
 * @returns {string|null} The current auth token, or null
 *
 * NOTE: In Clerk mode, this still returns the legacy mf_token from localStorage.
 * For Clerk JWTs, use getAuthHeaders() (async) instead.
 * EventSource/SSE callers that need Clerk tokens should use the async pattern
 * or accept that SSE falls back to legacy auth during Step 4.
 */
export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}


// ── Clerk signOut injection (Step 4 hardening — issue 5) ───────────────
// Set by ClerkTokenBridge via setClerkSignOut().
// Called by AuthContext.logout() to also sign out of Clerk.
let _clerkSignOut = null;

/**
 * Inject the Clerk signOut function from a React provider.
 * @param {Function|null} fn - Clerk's signOut(), or null to clear
 */
export function setClerkSignOut(fn) {
  _clerkSignOut = fn;
}

/**
 * Call Clerk signOut if injected. Safe no-op if not in Clerk mode.
 * @returns {Promise<void>}
 */
export async function clerkSignOut() {
  if (typeof _clerkSignOut === 'function') {
    try {
      await _clerkSignOut();
    } catch (err) {
      console.warn('[authHeaders] Clerk signOut failed:', err.message);
    }
  }
}
